import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Typography } from '@mui/material';
import { Accordion } from '@core/components';
import { getEntityById, getLabelFromId } from '@core/utils/collection';
import {
  StoleOptionFields,
  StolePanelFields,
} from '../../components/StoleValueFields';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { useCustomerById } from '../../hooks/useCustomerById';
import { applyStoleDesignPreset } from '../../utils/designPreset';
import {
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  type StoleItemFormValues,
  formatOrderItemSizeSummary,
  getMaterialSummary,
} from '../../utils/orderItem';
import { SIZE_FIELDS_BY_VESTMENT_CATEGORY } from '../../utils/size';
import { getStoleFeatures } from '../../utils/stole';
import { useDesignPresetPrompt } from './ChasubleItemDialog/useDesignPresetPrompt';
import { OrderItemSummaryRow } from './OrderItemDialog';

function StoleItemForm({
  methods: methodsProp,
  expandedSection,
  setExpandedSection,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<StoleItemFormValues>;
  const { vestments } = useAppContext();
  const vestment = getEntityById(vestments, methods.watch('vestment'));
  const features = getStoleFeatures(vestment?.type);
  const prevVestmentId = useRef(vestment?.id);
  const presetChooser = useDesignPresetPrompt(methods, (values) =>
    applyStoleDesignPreset(methods, values, vestment)
  );

  useEffect(() => {
    const prevId = prevVestmentId.current;
    prevVestmentId.current = vestment?.id;
    if (prevId == null || prevId === vestment?.id) {
      return;
    }
    if (methods.getValues('stoleCut')) {
      methods.setValue('stoleCut', '', { shouldDirty: true });
    }
  }, [methods, vestment]);

  return (
    <>
      <Accordion
        title="Panel"
        expanded={expandedSection === 'panel'}
        onChange={(expanded) => setExpandedSection(expanded ? 'panel' : null)}
      >
        <StolePanelFields<StoleItemFormValues>
          hasCuts={features?.hasCuts ?? false}
          cuts={vestment?.stoleCuts ?? []}
        />
      </Accordion>

      <Accordion
        title="Options"
        expanded={expandedSection === 'options'}
        onChange={(expanded) => setExpandedSection(expanded ? 'options' : null)}
      >
        <StoleOptionFields<StoleItemFormValues>
          patching={features?.patching !== false}
        />
      </Accordion>
      {presetChooser}
    </>
  );
}

function StoleItemSummary({
  methods: methodsProp,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<StoleItemFormValues>;
  const { vestments, sizes, fabrics, colors, cords, tassels } = useAppContext();

  const vestmentId = methods.watch('vestment');
  const quantity = methods.watch('quantity');
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const length = methods.watch('length');
  const stoleCutId = methods.watch('stoleCut');
  const panelFabricId = methods.watch('panelFabric');
  const panelColorId = methods.watch('panelColor');
  const withCord = methods.watch('withCord');
  const cordId = methods.watch('cord');
  const withTassel = methods.watch('withTassel');
  const tasselId = methods.watch('tassel');
  const withPatching = methods.watch('withPatching');
  const patchingFabricId = methods.watch('patchingFabric');
  const patchingColorId = methods.watch('patchingColor');

  const vestment = getEntityById(vestments, vestmentId);
  const features = getStoleFeatures(vestment?.type);
  const { data: forCustomer } = useCustomerById(forCustomerId);

  return (
    <>
      <OrderItemSummaryRow label="Quantity" fadeKey={quantity}>
        <Typography variant="caption" color="text.secondary">
          {quantity ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Size"
        fadeKey={`${sizeId}-${forCustomerId}-${length}`}
      >
        <Typography variant="caption" color="text.secondary">
          {formatOrderItemSizeSummary(
            SIZE_FIELDS_BY_VESTMENT_CATEGORY[VestmentCategory.Stole],
            {
              sizeName: getLabelFromId(sizes, sizeId),
              size: getEntityById(sizes, sizeId),
              customer: forCustomer,
              vestmentType: vestment?.type,
              length,
            }
          )}
        </Typography>
      </OrderItemSummaryRow>
      {features?.hasCuts ? (
        <OrderItemSummaryRow label="Type" fadeKey={stoleCutId}>
          <Typography variant="caption" color="text.secondary">
            {getLabelFromId(vestment?.stoleCuts ?? [], stoleCutId) ?? '-'}
          </Typography>
        </OrderItemSummaryRow>
      ) : null}
      <OrderItemSummaryRow
        label="Panel"
        fadeKey={`${panelFabricId}-${panelColorId}`}
        colorHex={getEntityById(colors, panelColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {getLabelFromId(fabrics, panelFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, panelColorId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow label="Cord" fadeKey={`${withCord}-${cordId}`}>
        <Typography variant="caption" color="text.secondary">
          {withCord ? (getMaterialSummary(cords, cordId) ?? '-') : 'Without'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow label="Tassel" fadeKey={`${withTassel}-${tasselId}`}>
        <Typography variant="caption" color="text.secondary">
          {withTassel
            ? (getMaterialSummary(tassels, tasselId) ?? '-')
            : 'Without'}
        </Typography>
      </OrderItemSummaryRow>
      {features?.patching === false ? null : (
        <OrderItemSummaryRow
          label="Patching"
          fadeKey={`${withPatching}-${patchingFabricId}-${patchingColorId}`}
          colorHex={
            withPatching
              ? getEntityById(colors, patchingColorId)?.hexCode
              : undefined
          }
        >
          <Typography variant="caption" color="text.secondary">
            {withPatching
              ? `${getLabelFromId(fabrics, patchingFabricId) ?? '-'} / ${getLabelFromId(colors, patchingColorId) ?? '-'}`
              : 'Without'}
          </Typography>
        </OrderItemSummaryRow>
      )}
    </>
  );
}

const StoleItemDialogContent = {
  renderForm: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <StoleItemForm {...slot} />
  ),
  renderSummary: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <StoleItemSummary {...slot} />
  ),
};

export default StoleItemDialogContent;
